import { Form, FormInstance } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { CopyBlock } from 'react-code-blocks';
import { format } from 'url';

import { CodeMethod } from '@page/explorer/components/code-method/code-method';
import { ExplorerContext } from '@page/explorer/explorer.provider';
import { CardExplorer } from '@page/explorer/explorer.style';
import { paramsReplacer, stringReplacer } from '@page/explorer/explorer.util';
import { PostmanItemURLInterface } from '@page/explorer/interface/postman-collection.interface';
import { getCodeLanguage, getTemplate, LanguageTemplateEnum } from '@page/explorer/templates';
import { initHeader } from '@page/explorer/templates/init.template';

import { Text } from '@atom/typography/typography';
import { MethodEnum } from '@enum/method.enum';
import { ColorEnum } from '@enum/style/color.enum';
import { isString } from '@util/data/is-exist';

import { codeExampleOptions } from './code-example.options';
import {
  CodeSelect,
  FullLink,
  MainLink,
  RunButton,
  themeCodeBlock,
  TitleWrapper,
  WrapperLink,
} from './code-example.style';

interface CodeExampleProps {
  url: PostmanItemURLInterface;
  method: MethodEnum;
  form: FormInstance;
  onClick: () => void;
  isLoading: boolean;
}

export const CodeExample = ({ onClick, form, method, url, isLoading }: CodeExampleProps) => {
  const { variables, currentEnv } = useContext(ExplorerContext);
  const [language, setLanguage] = useState(LanguageTemplateEnum.CURL);
  const body = Form.useWatch('body', form);
  const headers = Form.useWatch('headers', form);
  const params = Form.useWatch('params', form) ?? {};
  const query = (Form.useWatch('query', form) ?? {}) as Record<string, string>;

  const host = stringReplacer(url.host.at(0) ?? '', variables, currentEnv).replace(/^\/$/, '');
  const languageTemplate = getTemplate(language);

  const link =
    host +
    format({
      host: '/',
      pathname: url.path
        .map((path) => paramsReplacer(stringReplacer(path, variables, currentEnv), params, currentEnv))
        .join('/'),
      query: Object.fromEntries(Object.entries(query).filter(([, value]) => isString(value))),
    }).replace(/^\/+/, '/');

  const [text, setText] = useState(languageTemplate(link, initHeader, {}, method));
  const handleChange = (_: any, current: any) =>
    setText(languageTemplate(link, { ...initHeader, ...current.headers }, current.body, method));

  useEffect(
    () => setText(languageTemplate(link, { ...initHeader, ...headers }, body, method)),
    [link, body, headers, language, method],
  );

  return (
    <CardExplorer>
      <Form form={form} onValuesChange={handleChange} />
      <TitleWrapper>
        <WrapperLink>
          <CodeMethod method={method} /> <MainLink>{link.replace(host, '')}</MainLink>
        </WrapperLink>
        <CodeSelect
          options={codeExampleOptions}
          size='small'
          value={language}
          onChange={(language) => setLanguage(language as LanguageTemplateEnum)}
        />
      </TitleWrapper>
      <FullLink>{link}</FullLink>
      <CopyBlock language={getCodeLanguage(language)} text={text} {...themeCodeBlock} />
      <RunButton disabled={isLoading} loading={isLoading} onClick={onClick}>
        <Text color={ColorEnum.Inherit} level='l'>
          Run {'>'}
        </Text>
      </RunButton>
    </CardExplorer>
  );
};
